import { Capacitor } from '@capacitor/core';

let initialized = false;

export function initRevenueCat(): void {
  if (initialized) return;
  initialized = true;

  if (Capacitor.isNativePlatform()) {
    void import('@revenuecat/purchases-capacitor').then(({ Purchases }) => {
      const key = import.meta.env.VITE_REVENUECAT_IOS_API_KEY as string | undefined;
      if (key) {
        void Purchases.configure({ apiKey: key });
      }
    });
  } else {
    const key = import.meta.env.VITE_REVENUECAT_WEB_API_KEY as string | undefined;
    if (key) {
      void import('@revenuecat/purchases-js').then(({ Purchases }) => {
        Purchases.configure({ apiKey: key, appUserId: 'anonymous' });
      });
    }
  }
}

export async function loginRevenueCat(uid: string): Promise<void> {
  if (Capacitor.isNativePlatform()) {
    const { Purchases } = await import('@revenuecat/purchases-capacitor');
    await Purchases.logIn({ appUserID: uid });
  } else {
    const { Purchases } = await import('@revenuecat/purchases-js');
    const instance = Purchases.getSharedInstance();
    await instance.changeUser(uid);
  }
}

export async function logoutRevenueCat(): Promise<void> {
  if (Capacitor.isNativePlatform()) {
    const { Purchases } = await import('@revenuecat/purchases-capacitor');
    await Purchases.logOut();
  } else {
    // Web SDK has no server-side session logout; switching back to an anonymous
    // user is handled by reconfiguring on next launch. No-op for web.
  }
}

/**
 * Restore a prior purchase. Apple requires this control in the iOS binary for
 * auto-renewable subscriptions. On web, entitlements follow the logged-in
 * RevenueCat account (see {@link loginRevenueCat}) — there is no StoreKit-style
 * restore — so this is a no-op that resolves `false`.
 */
export async function restorePurchases(): Promise<boolean> {
  if (Capacitor.isNativePlatform()) {
    const { Purchases } = await import('@revenuecat/purchases-capacitor');
    const { customerInfo } = await Purchases.restorePurchases();
    return customerInfo.entitlements.active.pro !== undefined;
  }
  return false;
}

export interface PackageInfo {
  identifier: string;
  productId: string;
  priceString: string;
  period: 'monthly' | 'annual';
}

export async function getOfferings(): Promise<PackageInfo[]> {
  if (Capacitor.isNativePlatform()) {
    const { Purchases } = await import('@revenuecat/purchases-capacitor');
    const offerings = await Purchases.getOfferings();
    const current = offerings?.current;
    if (!current) return [];
    return (current.availablePackages ?? []).map((pkg) => ({
      identifier: pkg.identifier,
      productId: pkg.product.identifier,
      priceString: pkg.product.priceString,
      period: pkg.identifier === '$rc_annual' ? 'annual' : 'monthly',
    }));
  } else {
    const { Purchases } = await import('@revenuecat/purchases-js');
    const instance = Purchases.getSharedInstance();
    const offerings = await instance.getOfferings();
    const current = offerings.current;
    if (!current) return [];
    return current.availablePackages.map((pkg) => ({
      identifier: pkg.identifier,
      productId: pkg.rcBillingProduct.identifier,
      priceString: pkg.rcBillingProduct.currentPrice.formattedPrice,
      period: pkg.identifier === '$rc_annual' ? 'annual' : 'monthly',
    }));
  }
}

export async function purchasePackage(identifier: string): Promise<boolean> {
  if (Capacitor.isNativePlatform()) {
    const { Purchases } = await import('@revenuecat/purchases-capacitor');
    const offerings = await Purchases.getOfferings();
    const pkg = offerings?.current?.availablePackages?.find((p) => p.identifier === identifier);
    if (!pkg) throw new Error('Package not found');
    const result = await Purchases.purchasePackage({ aPackage: pkg });
    return result.customerInfo.entitlements.active.pro !== undefined;
  } else {
    const { Purchases } = await import('@revenuecat/purchases-js');
    const instance = Purchases.getSharedInstance();
    const offerings = await instance.getOfferings();
    const pkg = offerings.current?.availablePackages.find((p) => p.identifier === identifier);
    if (!pkg) throw new Error('Package not found');
    const { customerInfo } = await instance.purchase({ rcPackage: pkg });
    return customerInfo.entitlements.active.pro !== undefined;
  }
}
